import { createSlice } from "@reduxjs/toolkit";
import { getEmployeeList, getDeactivateUser, getCreateUser, putEditUserList, getUserById, getUploadList, getCampaignsList, getDepartmentList, getEntityList, getLocationList, getReportingPersonList, getRoleList, getExportUserList, getServicesList } from "../services/employee.service";

const initialState = {
  listLoader: false,
  deactivateLoader: false,
  editLoader: false,
  userIdLoader: false,
  roleListLoader: false,
  exportLoader: false,
  uploadListLoader: false,
  listData: [],
  deactivateListData: [],
  userIdListData: [],
  editListData: [],
  uploadListData: [],
  campaignsListData: [],
  servicesListData: [],
  exportData: [],
  roleListData: [],
  reportingPersonListData: [],
  departmentListData: [],
  locationListData: [],
  entityListData: [],
  totalListCount: 0,
  listDataMsg: "",
  listDataError: "",
  deactivateErrorMsg: "",
  deactivateSuccessMsg: "",
  editErrorMsg: "",
  editSuccessMsg: "",
  userIdError: "",
  locationListLoader: false, // Loader to track the API request status
  reportingPersonListLoader: false, // Loader to track the API request status
  departmentListLoader: false, // Loader to track the API request status
  campaignsListLoader: false, // Loader to track the API request status
  servicesListLoader: false, // Loader to track the API request status
  entityListLoader: false, // Loader to track the API request status
  createUserLoader: false, // Loader to track the API request status
  createUserSuccessMsg: "", // Success message after user creation
  createUserErrorMsg: "", // Error message for user creation
  uploadError: "", // Error message for upload
  uploadSuccessMsg: "", // success message for upload
  campaignsError: "", // Error message for campaign
  servicesError: "", // Error message for services
  entityError: "", // Error message for entity
  departmentError: "", // Error message for department
  locationError: "", // Error message for location
  reportingPersonError: "", // Error message for reporting person
  roleError: "", // Error message for role error
  exportError: "", // Error message for export error
  isCreateUserSuccess: false,
  createdUser: null,
};

const EmployeeSlice = createSlice({
  name: "employee-list",
  initialState: initialState,
  reducers: {
    clearUserMessages: (state) => {
      // Clear all success/error messages
      state.createUserSuccessMsg = '';
      state.createUserErrorMsg = '';
      state.editSuccessMsg = '';
      state.editErrorMsg = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeList.pending, (state, action) => {
        state.listLoader = true;
      })
      .addCase(getEmployeeList.fulfilled, (state, action) => {
        state.listLoader = false;
        state.listData = action.payload?.results;
        state.listDataMsg = "Employee list retrieved successfully!";
        state.totalListCount = action.payload?.count;
      })
      .addCase(getEmployeeList.rejected, (state, action) => {
        state.listLoader = false;
        state.listDataError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getDeactivateUser.pending, (state, action) => {
        state.deactivateLoader = true;
      })
      .addCase(getDeactivateUser.fulfilled, (state, action) => {
        state.deactivateLoader = false;
        state.deactivateListData = action.payload?.data;
        state.deactivateSuccessMsg = 'Deleted record successfully!'
      })
      .addCase(getDeactivateUser.rejected, (state, action) => {
        state.deactivateLoader = false;
        state.deactivateErrorMsg =
          state.deactivateErrorMsg =
          action.error?.message || "Failed to load employee list";
      })
      // Create User Cases
      .addCase(getCreateUser.pending, (state) => {
        state.createUserLoader = true;
        state.createUserSuccessMsg = ""; // Clear previous success message
        state.createUserErrorMsg = ""; // Clear previous error message
        state.isCreateUserSuccess = false; // Reset success flag
      })
      .addCase(getCreateUser.fulfilled, (state, action: any) => {
        state.createUserLoader = false;
        state.createdUser = action.payload?.data; // Store created user data
        state.createUserSuccessMsg = action.payload?.message || "User created successfully!";
        state.createUserErrorMsg = ""; // Clear error message
        state.isCreateUserSuccess = true;
      })
      .addCase(getCreateUser.rejected, (state, action: any) => {
        state.createUserLoader = false; // Set loader to false
        // Ensure the error is a string
        state.createUserErrorMsg = action.payload
          ? action.payload
          : "Failed to create user";
        state.createUserSuccessMsg = ""; // Clear any success message
      })
      .addCase(putEditUserList.pending, (state, action) => {
        state.editLoader = true;
      })
      .addCase(putEditUserList.fulfilled, (state, action: any) => {
        state.editLoader = false;
        state.editListData = action.payload?.data;
        state.editSuccessMsg = action.payload?.message || "User updated successfully";
      })
      .addCase(putEditUserList.rejected, (state, action: any) => {
        state.editLoader = false;
        state.editErrorMsg = action.payload?.data || "Failed to load employee list";
      })
      .addCase(getUserById.pending, (state, action) => {
        state.userIdLoader = true;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.userIdLoader = false;
        state.userIdListData = action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.userIdLoader = false;
        state.userIdError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getUploadList.pending, (state, action) => {
        state.uploadListLoader = true;
      })
      .addCase(getUploadList.fulfilled, (state, action: any) => {
        state.uploadListLoader = false;
        state.uploadListData = action.payload.data;
        state.uploadSuccessMsg = action.payload?.message;
      })
      .addCase(getUploadList.rejected, (state: any, action) => {
        state.uploadListLoader = false;
        state.uploadError = action.payload || "Failed to load employee list";
      })
      .addCase(getCampaignsList.pending, (state, action) => {
        state.campaignsListLoader = true;
      })
      .addCase(getCampaignsList.fulfilled, (state, action) => {
        state.campaignsListLoader = false;
        state.campaignsListData = action.payload.results;
      })
      .addCase(getCampaignsList.rejected, (state, action) => {
        state.campaignsListLoader = false;
        state.campaignsError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getServicesList.pending, (state, action) => {
        state.servicesListLoader = true;
      })
      .addCase(getServicesList.fulfilled, (state, action) => {
        state.servicesListLoader = false;
        state.servicesListData = action.payload.results;
      })
      .addCase(getServicesList.rejected, (state, action) => {
        state.servicesListLoader = false;
        state.servicesError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getEntityList.pending, (state, action) => {
        state.entityListLoader = true;
      })
      .addCase(getEntityList.fulfilled, (state, action) => {
        state.entityListLoader = false;
        state.entityListData = action.payload.results;
      })
      .addCase(getEntityList.rejected, (state, action) => {
        state.entityListLoader = false;
        state.entityError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getReportingPersonList.pending, (state, action) => {
        state.reportingPersonListLoader = true;
      })
      .addCase(getReportingPersonList.fulfilled, (state, action) => {
        state.reportingPersonListLoader = false;
        state.reportingPersonListData = action.payload.results;
      })
      .addCase(getReportingPersonList.rejected, (state, action) => {
        state.reportingPersonListLoader = false;
        state.reportingPersonError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getDepartmentList.pending, (state, action) => {
        state.departmentListLoader = true;
      })
      .addCase(getDepartmentList.fulfilled, (state, action) => {
        state.departmentListLoader = false;
        state.departmentListData = action.payload.results;
      })
      .addCase(getDepartmentList.rejected, (state, action) => {
        state.departmentListLoader = false;
        state.departmentError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getLocationList.pending, (state, action) => {
        state.locationListLoader = true;
      })
      .addCase(getLocationList.fulfilled, (state, action) => {
        state.locationListLoader = false;
        state.locationListData = action.payload.results;
      })
      .addCase(getLocationList.rejected, (state, action) => {
        state.locationListLoader = false;
        state.locationError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getRoleList.pending, (state, action) => {
        state.roleListLoader = true;
      })
      .addCase(getRoleList.fulfilled, (state, action) => {
        state.roleListLoader = false;
        state.roleListData = action.payload.results;
      })
      .addCase(getRoleList.rejected, (state, action) => {
        state.roleListLoader = false;
        state.roleError = action.error?.message || "Failed to load employee list";
      })
      .addCase(getExportUserList.pending, (state, action) => {
        state.exportLoader = true;
      })
      .addCase(getExportUserList.fulfilled, (state, action) => {
        state.exportLoader = false;
        state.exportData = action.payload.results;
      })
      .addCase(getExportUserList.rejected, (state, action) => {
        state.exportLoader = false;
        state.exportError = action.error?.message || "Failed to load employee list";
      })
  },
});

export default EmployeeSlice.reducer;
export const { clearUserMessages } = EmployeeSlice.actions;
